import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import {
  LayoutDashboard,
  CalendarDays,
  Briefcase,
  DollarSign,
  FileText,
  Receipt,
  Users,
  Truck,
  PieChart,
  Bell,
  Menu,
  X,
  Brain,
  Settings,
  AlertTriangle,
  Clock,
  CheckCircle2,
} from 'lucide-react';
import { cn } from '../lib/utils';
import { useData } from '../contexts/DataContext';
import { useAuth } from '../contexts/AuthContext';
import { buildNotifications, formatNotificationDate, type AppNotification } from '../lib/notifications';
import { MentorAlertBanner } from './mentor/MentorAlertBanner';
import { GuideChatFab } from './guide/GuideChatFab';

const navItems = [
  { name: 'Dashboard', path: '/', icon: LayoutDashboard },
  { name: 'Agenda', path: '/agenda', icon: CalendarDays },
  { name: 'Contratos', path: '/contratos', icon: Briefcase },
  { name: 'Financeiro', path: '/financeiro', icon: DollarSign, sensitive: true },
  { name: 'Orçamentos', path: '/orcamentos', icon: FileText },
  { name: 'Recibos', path: '/recibos', icon: Receipt },
  { name: 'Prestadores', path: '/prestadores', icon: Users },
  { name: 'Fornecedores', path: '/fornecedores', icon: Truck },
  { name: 'Relatórios', path: '/relatorios', icon: PieChart, sensitive: true },
  { name: 'Meu Mentor', path: '/mentor', icon: Brain, sensitive: true },
  { name: 'Configurações', path: '/configuracoes', icon: Settings },
];

export default function Layout({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { logout, role } = useAuth();
  const visibleNavItems = React.useMemo(
    () => (role === 'funcionario' ? navItems.filter((item) => !item.sensitive) : navItems),
    [role],
  );
  const { contracts: allContracts, transactions } = useData();
  const contracts = React.useMemo(
    () => allContracts.filter((contract) => contract.agendaOnly !== true),
    [allContracts],
  );
  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [sidebarLogo, setSidebarLogo] = React.useState<string | null>(() => localStorage.getItem('@helps:logo'));
  const [sidebarProfile, setSidebarProfile] = React.useState<{
    ownerName: string;
    ownerEmail: string;
    companyName: string;
  }>({
    ownerName: '',
    ownerEmail: '',
    companyName: '',
  });

  const syncBranding = React.useCallback((payload: Partial<{
    ownerName: string;
    ownerEmail: string;
    companyName: string;
    logoBase64: string | null;
  }>) => {
    setSidebarProfile((current) => ({
      ownerName: payload.ownerName ?? current.ownerName,
      ownerEmail: payload.ownerEmail ?? current.ownerEmail,
      companyName: payload.companyName ?? current.companyName,
    }));

    if (payload.logoBase64 !== undefined) {
      setSidebarLogo(payload.logoBase64);
    }
  }, []);

  // Listen for logo changes
  React.useEffect(() => {
    let ignore = false;

    const loadProfile = async () => {
      try {
        const response = await fetch('/api/settings/profile');
        if (!response.ok) {
          return;
        }

        const payload = await response.json() as {
          ownerName?: string;
          ownerEmail?: string;
          companyName?: string;
          logoBase64?: string | null;
        };

        if (ignore) {
          return;
        }

        syncBranding({
          ownerName: payload.ownerName ?? '',
          ownerEmail: payload.ownerEmail ?? '',
          companyName: payload.companyName ?? '',
          logoBase64: payload.logoBase64 ?? null,
        });
      } catch {
        // Mantemos o fallback visual do layout.
      }
    };

    const onStorage = (e: StorageEvent) => {
      if (e.key === '@helps:logo') setSidebarLogo(e.newValue);
    };
    const onBrandingUpdate = (event: Event) => {
      const customEvent = event as CustomEvent<{
        ownerName?: string;
        ownerEmail?: string;
        companyName?: string;
        logoBase64?: string | null;
      }>;
      syncBranding(customEvent.detail ?? {});
    };

    void loadProfile();
    window.addEventListener('storage', onStorage);
    window.addEventListener('helps:branding-updated', onBrandingUpdate as EventListener);
    return () => {
      ignore = true;
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('helps:branding-updated', onBrandingUpdate as EventListener);
    };
  }, [syncBranding]);
  const [isNotificationsOpen, setIsNotificationsOpen] = React.useState(false);
  const [mentorNotifications, setMentorNotifications] = React.useState<AppNotification[]>([]);
  const [readNotificationIds, setReadNotificationIds] = React.useState<string[]>(() => {
    try {
      const rawValue = localStorage.getItem('@helps:notification-read');
      const parsed = rawValue ? JSON.parse(rawValue) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  });
  const [activeUrgentToasts, setActiveUrgentToasts] = React.useState<AppNotification[]>([]);
  const notificationsPanelRef = React.useRef<HTMLDivElement>(null);
  const toastTimersRef = React.useRef<Record<string, number>>({});

  const notifications = React.useMemo(
    () => buildNotifications(contracts, transactions, mentorNotifications),
    [contracts, mentorNotifications, transactions],
  );
  const unreadCount = notifications.filter((notification) => !readNotificationIds.includes(notification.id)).length;

  React.useEffect(() => {
    let isMounted = true;

    const loadMentorNotifications = async () => {
      try {
        const response = await fetch('/api/mentor/notifications');
        if (!response.ok) {
          return;
        }

        const payload = await response.json() as unknown;
        if (!isMounted || !Array.isArray(payload)) {
          return;
        }

        setMentorNotifications(payload as AppNotification[]);
      } catch {
        // Mantemos o último snapshot válido do sino se a rede oscilar.
      }
    };

    void loadMentorNotifications();
    const intervalId = window.setInterval(() => {
      void loadMentorNotifications();
    }, 60_000);

    return () => {
      isMounted = false;
      window.clearInterval(intervalId);
    };
  }, [location.pathname]);

  const markNotificationAsRead = React.useCallback((notificationId: string) => {
    setReadNotificationIds((currentIds) => {
      if (currentIds.includes(notificationId)) {
        return currentIds;
      }

      const nextIds = [...currentIds, notificationId];
      localStorage.setItem('@helps:notification-read', JSON.stringify(nextIds));
      return nextIds;
    });
  }, []);

  const markAllNotificationsAsRead = React.useCallback(() => {
    const allIds = notifications.map((notification) => notification.id);
    setReadNotificationIds(allIds);
    localStorage.setItem('@helps:notification-read', JSON.stringify(allIds));
  }, [notifications]);

  const dismissUrgentToast = React.useCallback((notificationId: string) => {
    setActiveUrgentToasts((currentToasts) =>
      currentToasts.filter((notification) => notification.id !== notificationId),
    );

    const timerId = toastTimersRef.current[notificationId];
    if (timerId) {
      window.clearTimeout(timerId);
      delete toastTimersRef.current[notificationId];
    }
  }, []);

  React.useEffect(() => {
    localStorage.setItem(
      '@helps:notification-read',
      JSON.stringify(readNotificationIds.filter((notificationId) =>
        notifications.some((notification) => notification.id === notificationId),
      )),
    );
  }, [notifications, readNotificationIds]);

  React.useEffect(() => {
    const handleOutsideClick = (event: MouseEvent) => {
      if (!notificationsPanelRef.current?.contains(event.target as Node)) {
        setIsNotificationsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleOutsideClick);
    return () => document.removeEventListener('mousedown', handleOutsideClick);
  }, []);

  React.useEffect(() => {
    const sessionStorageKey = '@helps:seen-urgent-notifications';
    const seenUrgentIds = new Set<string>(
      JSON.parse(sessionStorage.getItem(sessionStorageKey) ?? '[]'),
    );

    const unseenUrgentNotifications = notifications.filter(
      (notification) => notification.severity === 'urgent' && !seenUrgentIds.has(notification.id),
    );

    if (unseenUrgentNotifications.length === 0) {
      return;
    }

    setActiveUrgentToasts((currentToasts) => {
      const currentIds = new Set(currentToasts.map((notification) => notification.id));
      return [
        ...currentToasts,
        ...unseenUrgentNotifications.filter((notification) => !currentIds.has(notification.id)),
      ].slice(0, 3);
    });

    unseenUrgentNotifications.forEach((notification) => {
      seenUrgentIds.add(notification.id);
      toastTimersRef.current[notification.id] = window.setTimeout(() => {
        dismissUrgentToast(notification.id);
      }, 8000);
    });

    sessionStorage.setItem(sessionStorageKey, JSON.stringify(Array.from(seenUrgentIds)));
  }, [dismissUrgentToast, notifications]);

  React.useEffect(
    () => () => {
      Object.values(toastTimersRef.current).forEach((timerId) => window.clearTimeout(Number(timerId)));
    },
    [],
  );

  const handleNotificationOpen = () => {
    setIsNotificationsOpen((current) => !current);
  };

  const handleNotificationClick = (notification: AppNotification) => {
    markNotificationAsRead(notification.id);
    setIsNotificationsOpen(false);
    navigate(notification.href);
  };

  const getNotificationIcon = (notification: AppNotification) => {
    if (notification.category === 'mentor' && notification.severity === 'info') {
      return <Brain className="w-4 h-4 text-emerald-600" />;
    }
    if (notification.severity === 'urgent') {
      return <AlertTriangle className="w-4 h-4 text-amber-600" />;
    }
    if (notification.severity === 'warning') {
      return <Clock className="w-4 h-4 text-amber-600" />;
    }
    return <CheckCircle2 className="w-4 h-4 text-emerald-600" />;
  };

  const getNotificationAccentClass = (notification: AppNotification) => {
    if (notification.severity === 'urgent') {
      return 'border-amber-200 bg-amber-50/60';
    }
    if (notification.severity === 'warning') {
      return 'border-amber-200 bg-amber-50/70';
    }
    return 'border-emerald-200 bg-emerald-50/70';
  };

  const sidebarDisplayName = sidebarProfile.ownerName || sidebarProfile.companyName || 'Conta principal';
  const sidebarDisplayEmail = sidebarProfile.ownerEmail || 'Atualize o e-mail em Configurações';
  const sidebarInitialsSource = sidebarProfile.ownerName || sidebarProfile.companyName || 'Helps';
  const sidebarInitials = sidebarInitialsSource
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((segment) => segment[0]?.toUpperCase() ?? '')
    .join('') || 'H';

  const handleLogout = async () => {
    await logout();
    navigate('/login', { replace: true });
  };

  const NavLinks = ({ mobile = false }: { mobile?: boolean }) => (
    <>
      <div className="px-3 text-xs font-semibold text-white/40 uppercase tracking-wider mb-2">Menu Principal</div>
      <nav className="space-y-1 px-2">
        {visibleNavItems.map((item) => {
          const isActive = location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path));
          const isMentorItem = item.path === '/mentor';
          return (
            <Link
              key={item.name}
              to={item.path}
              onClick={() => mobile && setIsMobileMenuOpen(false)}
              className={cn(
                "flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 text-sm font-medium relative",
                isActive 
                  ? "text-brand-gold bg-white/5" 
                  : "text-white/60 hover:bg-white/5 hover:text-white"
              )}
            >
              {isActive && (
                <motion.div 
                  layoutId="activeNav"
                  className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-5 bg-brand-gold rounded-r-full"
                />
              )}
              <span className="relative flex h-5 w-5 items-center justify-center">
                {isMentorItem ? (
                  <>
                    <span
                      className={cn(
                        "mentor-sidebar-glow absolute inset-[-5px] rounded-full bg-emerald-400/20 blur-md transition-opacity duration-300",
                        isActive ? "opacity-100" : "opacity-75",
                      )}
                    />
                    <span
                      className={cn(
                        "absolute inset-[-2px] rounded-full ring-1 ring-emerald-400/35",
                        isActive
                          ? "shadow-[0_0_14px_rgba(74,222,128,0.28)]"
                          : "shadow-[0_0_10px_rgba(74,222,128,0.18)]",
                      )}
                    />
                  </>
                ) : null}
                <item.icon
                  className={cn(
                    "relative z-10 w-4 h-4 transition-colors",
                    isActive
                      ? "text-brand-gold"
                      : isMentorItem
                        ? "text-emerald-300"
                        : "text-white/50",
                  )}
                />
              </span>
              {item.name}
            </Link>
          );
        })}
      </nav>
    </>
  );

  return (
    <div className="flex h-screen overflow-hidden bg-brand-bg font-sans text-gray-800 selection:bg-brand-gold/30">
      {/* Sidebar Desktop */}
      <aside className="hidden md:flex flex-col w-64 h-screen shrink-0 bg-brand-dark text-white border-r border-white/10 z-50 shadow-2xl">
        <div className="h-20 flex items-center px-6">
          <Link to="/" className="flex items-center gap-3 group">
            {sidebarLogo ? (
              <img src={sidebarLogo} alt="Logo" className="w-9 h-9 rounded-xl object-contain shadow-lg group-hover:scale-105 transition-transform" />
            ) : (
              <div className="w-9 h-9 bg-gradient-to-br from-brand-gold to-brand-yellow rounded-xl flex items-center justify-center font-display font-bold text-brand-dark text-xl shadow-lg group-hover:scale-105 transition-transform">
                H
              </div>
            )}
            <span className="text-2xl font-display font-bold tracking-tight text-white">Helps</span>
          </Link>
        </div>

        <div className="flex-1 py-4 overflow-y-auto scrollbar-hide">
          <NavLinks />
        </div>

        <div className="p-4 m-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm relative group">
          <Link
            to="/configuracoes"
            className="absolute top-3 right-3 p-1.5 text-white/40 hover:text-brand-gold hover:bg-white/10 rounded-lg transition-colors"
            title="Configurações"
          >
            <Settings className="w-4 h-4" />
          </Link>
          <div className="flex items-center gap-3">
            {sidebarLogo ? (
              <img src={sidebarLogo} alt="" className="w-9 h-9 rounded-full object-contain border border-white/20" />
            ) : (
              <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-brand-dark to-brand-dark/80 border border-white/20 flex items-center justify-center text-sm font-medium text-brand-light">
                {sidebarInitials}
              </div>
            )}
            <div className="flex-1 min-w-0 pr-6">
              <p className="text-sm font-medium text-white truncate">{sidebarDisplayName}</p>
              <p className="text-xs text-white/50 truncate">{sidebarDisplayEmail}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={handleLogout}
            className="mt-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
          >
            Sair do painel
          </button>
        </div>
      </aside>

      {/* Mobile Menu */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-40 bg-brand-dark/80 backdrop-blur-sm md:hidden" 
              onClick={() => setIsMobileMenuOpen(false)} 
            />
            <motion.aside 
              initial={{ x: '-100%' }}
              animate={{ x: 0 }}
              exit={{ x: '-100%' }}
              transition={{ type: 'spring', damping: 25, stiffness: 200 }}
              className="fixed inset-y-0 left-0 w-72 bg-brand-dark text-white flex flex-col z-50 shadow-2xl border-r border-white/10"
            >
              <div className="h-20 flex items-center justify-between px-6">
                <div className="flex items-center gap-3">
                  {sidebarLogo ? (
                    <img src={sidebarLogo} alt="Logo" className="w-9 h-9 rounded-xl object-contain" />
                  ) : (
                    <div className="w-9 h-9 bg-gradient-to-br from-brand-gold to-brand-yellow rounded-xl flex items-center justify-center font-display font-bold text-brand-dark text-xl">
                      H
                    </div>
                  )}
                  <span className="text-2xl font-display font-bold tracking-tight">Helps</span>
                </div>
                <button onClick={() => setIsMobileMenuOpen(false)} className="p-2 text-white/50 hover:text-white transition-colors rounded-lg hover:bg-white/10">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="flex-1 py-4 overflow-y-auto">
                <NavLinks mobile />
              </div>
              <div className="p-4 m-4 rounded-xl bg-white/5 border border-white/10 backdrop-blur-sm relative">
                <Link 
                  to="/configuracoes" 
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="absolute top-3 right-3 p-1.5 text-white/40 hover:text-brand-gold hover:bg-white/10 rounded-lg transition-colors"
                >
                  <Settings className="w-4 h-4" />
                </Link>
                <div className="flex items-center gap-3">
                  {sidebarLogo ? (
                    <img src={sidebarLogo} alt="" className="w-9 h-9 rounded-full object-contain border border-white/20" />
                  ) : (
                    <div className="w-9 h-9 rounded-full bg-gradient-to-tr from-brand-dark to-brand-dark/80 border border-white/20 flex items-center justify-center text-sm font-medium text-brand-light">
                      {sidebarInitials}
                    </div>
                  )}
                  <div className="flex-1 min-w-0 pr-6">
                    <p className="text-sm font-medium text-white truncate">{sidebarDisplayName}</p>
                    <p className="text-xs text-white/50 truncate">{sidebarDisplayEmail}</p>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="mt-3 w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-xs font-medium text-white/70 hover:bg-white/10 hover:text-white transition-colors"
                >
                  Sair do painel
                </button>
              </div>
            </motion.aside>
          </>
        )}
      </AnimatePresence>

      {/* Main Content */}
      <main className="flex flex-1 flex-col overflow-hidden relative">
        {/* Decorative background element */}
        <div className="absolute top-0 left-0 right-0 h-96 bg-gradient-to-b from-brand-dark/[0.03] to-transparent pointer-events-none -z-10" />

        <header className="h-20 glass-panel border-b border-gray-200/50 flex items-center justify-between px-4 sm:px-8 shrink-0 z-30">
          <div className="flex items-center gap-4">
            <button 
              className="md:hidden p-2 -ml-2 text-gray-500 hover:text-brand-dark transition-colors rounded-lg hover:bg-gray-100"
              onClick={() => setIsMobileMenuOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <div className="hidden sm:flex items-center gap-2 text-sm text-gray-500 font-medium">
              <span className="text-brand-dark/40">Helps</span>
              <span className="text-brand-dark/30">/</span>
              <span className="text-brand-dark font-semibold">
                {navItems.find(item => location.pathname === item.path || (item.path !== '/' && location.pathname.startsWith(item.path)))?.name || (location.pathname === '/ia' ? 'Meu Mentor' : 'Sistema')}
              </span>
            </div>
          </div>
          
          <div className="flex items-center gap-3 sm:gap-5">
            <div className="relative" ref={notificationsPanelRef}>
              <button
                onClick={handleNotificationOpen}
                title="Ver notificações"
                className="relative p-2 text-gray-400 hover:text-brand-dark transition-colors rounded-full hover:bg-gray-100"
              >
                <Bell className="w-5 h-5" />
                {notifications.length > 0 && (
                  unreadCount > 0 ? (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-brand-dark text-[10px] leading-[18px] text-white font-bold border-2 border-white text-center">
                      {Math.min(unreadCount, 9)}
                    </span>
                  ) : (
                    <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-brand-gold rounded-full border-2 border-white"></span>
                  )
                )}
              </button>

              <AnimatePresence>
                {isNotificationsOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: -8, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: -8, scale: 0.98 }}
                    transition={{ duration: 0.18 }}
                    className="absolute right-0 top-14 w-[min(24rem,calc(100vw-2rem))] rounded-2xl border border-gray-200 bg-white shadow-[0_24px_48px_-24px_rgba(15,23,42,0.3)] overflow-hidden"
                  >
                    <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/80">
                      <div>
                        <p className="text-sm font-semibold text-gray-900">Notificações</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {notifications.length > 0
                            ? `${notifications.length} alerta${notifications.length === 1 ? '' : 's'} ativos`
                            : 'Nenhum alerta no momento'}
                        </p>
                      </div>
                      {notifications.length > 0 && unreadCount > 0 && (
                        <button
                          type="button"
                          onClick={markAllNotificationsAsRead}
                          className="text-xs font-medium text-brand-dark hover:text-brand-gold transition-colors"
                        >
                          Marcar todas como lidas
                        </button>
                      )}
                    </div>

                    {notifications.length > 0 ? (
                      <div className="max-h-[26rem] overflow-y-auto p-3 space-y-3">
                        {notifications.map((notification) => {
                          const isRead = readNotificationIds.includes(notification.id);

                          return (
                            <button
                              key={notification.id}
                              type="button"
                              onClick={() => handleNotificationClick(notification)}
                              className={cn(
                                'w-full text-left rounded-2xl border p-3 transition-all hover:shadow-sm',
                                getNotificationAccentClass(notification),
                                isRead ? 'opacity-80' : 'ring-1 ring-brand-dark/5',
                              )}
                            >
                              <div className="flex items-start gap-3">
                                <div className="mt-0.5 w-9 h-9 rounded-full bg-white/90 border border-white flex items-center justify-center shrink-0">
                                  {getNotificationIcon(notification)}
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="flex items-center justify-between gap-3">
                                    <p className="text-sm font-semibold text-gray-900 truncate">{notification.title}</p>
                                    <span className="text-[11px] font-medium text-gray-500 shrink-0">
                                      {formatNotificationDate(notification.createdAt)}
                                    </span>
                                  </div>
                                  <p className="text-sm text-gray-600 mt-1 leading-relaxed">{notification.message}</p>
                                  <div className="flex items-center justify-between gap-3 mt-3">
                                    <span className="inline-flex items-center px-2.5 py-1 rounded-full bg-white/80 border border-white text-[11px] font-semibold text-gray-700">
                                      {notification.badgeLabel}
                                    </span>
                                    <span className="text-xs font-medium text-brand-dark">
                                      Abrir
                                    </span>
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    ) : (
                      <div className="px-4 py-10 text-center">
                        <div className="w-12 h-12 mx-auto rounded-full bg-gray-100 flex items-center justify-center mb-3">
                          <Bell className="w-5 h-5 text-gray-400" />
                        </div>
                        <p className="text-sm font-medium text-gray-700">Tudo sob controle</p>
                        <p className="text-xs text-gray-500 mt-1">Quando houver algo importante, aparece aqui.</p>
                      </div>
                    )}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </header>

        {role !== 'funcionario' && location.pathname !== '/' && location.pathname !== '/mentor' ? <MentorAlertBanner /> : null}

        <div className="flex-1 overflow-y-auto p-4 sm:p-8">
          <div className="max-w-7xl mx-auto pb-12">
            {children}
          </div>
        </div>

        <GuideChatFab />

        <div className="pointer-events-none fixed top-24 right-4 sm:right-8 z-40 flex flex-col gap-3 w-[min(24rem,calc(100vw-2rem))]">
          <AnimatePresence>
            {activeUrgentToasts.map((notification) => (
              <motion.div
                key={notification.id}
                initial={{ opacity: 0, x: 24, y: -8 }}
                animate={{ opacity: 1, x: 0, y: 0 }}
                exit={{ opacity: 0, x: 16, y: -8 }}
                transition={{ duration: 0.22 }}
                className="pointer-events-auto rounded-2xl border border-rose-200 bg-white/95 backdrop-blur-sm shadow-[0_22px_40px_-24px_rgba(159,18,57,0.25)] overflow-hidden"
              >
                <div className="h-1.5 bg-rose-500" />
                <div className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-rose-50 flex items-center justify-center shrink-0">
                      <AlertTriangle className="w-5 h-5 text-rose-600" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="text-sm font-semibold text-gray-900">{notification.title}</p>
                          <p className="text-xs font-medium text-rose-600 mt-0.5">Alerta urgente</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => dismissUrgentToast(notification.id)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                      <p className="text-sm text-gray-600 mt-2 leading-relaxed">{notification.message}</p>
                      <div className="mt-3 flex items-center gap-3">
                        <button
                          type="button"
                          onClick={() => {
                            dismissUrgentToast(notification.id);
                            handleNotificationClick(notification);
                          }}
                          className="px-3 py-1.5 rounded-lg bg-brand-dark text-white text-xs font-semibold hover:bg-brand-dark/90 transition-colors"
                        >
                          Ver agora
                        </button>
                        <span className="text-[11px] text-gray-400">
                          some automaticamente em alguns segundos
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </main>
    </div>
  );
}
